import { filter, includes, intersection, isEmpty, map } from 'lodash';
import React, { useMemo } from 'react';

import { FormikSelect, FormikSelectProps } from 'components';
import { useUserContext } from 'hooks';
import { formUtils } from 'utils';

type Props = {
  name: string;
  label: string;
  assetCurrencyIds?: string[];
  exclude?: string[];
  tradeMethodsIds?: string[];
  ignoreTradeMethodsIds?: boolean;
  hideArchived?: boolean;
} & Partial<FormikSelectProps>;

export const P2PProviderSelect: React.FC<Props> = ({
  name,
  label,
  assetCurrencyIds,
  exclude = [],
  tradeMethodsIds = [],
  ignoreTradeMethodsIds = false,
  hideArchived = false,
  ...rest
}) => {
  const { p2pProviders } = useUserContext();
  const availableP2PProviders = useMemo(
    () =>
      filter(p2pProviders, (p2pProvider) => {
        const p2pProviderTradeMethodsId = map(
          [
            ...(p2pProvider?.payinConfig?.tradeMethods || []),
            ...(p2pProvider?.payoutConfig?.tradeMethods || []),
          ],
          ({ id }) => id,
        );

        if (
          !ignoreTradeMethodsIds &&
          !intersection(tradeMethodsIds, p2pProviderTradeMethodsId).length
        ) {
          return false;
        }

        if (
          !isEmpty(assetCurrencyIds) &&
          !includes(assetCurrencyIds, p2pProvider.assetCurrencyId)
        ) {
          return false;
        }

        if (exclude?.includes(p2pProvider.id)) {
          return false;
        }

        if (hideArchived && p2pProvider.archived) {
          return false;
        }

        return true;
      }),
    [
      p2pProviders,
      tradeMethodsIds,
      assetCurrencyIds,
      exclude,
      ignoreTradeMethodsIds,
      hideArchived,
    ],
  );

  const providersOptions = useMemo(
    () => formUtils.getOptions(availableP2PProviders),
    [availableP2PProviders],
  );

  return (
    <FormikSelect
      noneOption
      label={label}
      {...rest}
      name={name}
      options={providersOptions}
    />
  );
};
